/**
 * What Today hands the game when you press Play: the puzzle it already loaded (so the
 * game mounts in the same frame, no loading state), where the hero board was on screen
 * (the FLIP's first frame), and the ghost's selection (your first clue). In memory only:
 * a reload or a deep link simply has no hand-over and opens the game plainly.
 */
import type { DailyLevels, DailyPuzzle } from '../api/client.ts';

export interface Handoff {
	/** The storage slot of the game it's for (`daily:<date>:<level>`). */
	slot: string;
	/** The hero board's box on screen when Play was pressed. */
	rect: { left: number; top: number; width: number; height: number } | null;
	/** The answer the attract ghost was on: it becomes the first selection. */
	ghost: string | null;
	/** "See your result": open on the poster. */
	openResult: boolean;
	at: number;
}

let pending: Handoff | null = null;

export function setHandoff(h: Handoff): void {
	pending = h;
}

/** The hand-over for `slot`, if there's a fresh one (pure: StrictMode may ask twice). */
export function peekHandoff(slot: string, now = Date.now()): Handoff | null {
	const h = pending;
	return h && h.slot === slot && now - h.at <= 4000 ? h : null;
}

/** The game it was for has opened: it's used up. */
export function clearHandoff(h: Handoff | null): void {
	if (h && pending === h) pending = null;
}

// --- Loaded dailies, so Today ⇄ game never refetches what's on screen -------------------

const dailies = new Map<string, DailyPuzzle>();
const levels = new Map<string, DailyLevels>();

export function cacheDaily(d: DailyPuzzle): void {
	dailies.set(`${d.date}:${d.level}`, d);
	dailies.set(`#${d.number}:${d.level}`, d);
}

export function cachedDaily(key: { date: string } | { number: number }, level: string): DailyPuzzle | undefined {
	return dailies.get('date' in key ? `${key.date}:${level}` : `#${key.number}:${level}`);
}

export function cacheLevels(l: DailyLevels): void {
	levels.set(l.date, l);
}

export function cachedLevels(date: string): DailyLevels | undefined {
	return levels.get(date);
}

/** Tests: forget everything. */
export function resetHandoffCache(): void {
	pending = null;
	dailies.clear();
	levels.clear();
}
